"use client";
import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import {
  ArrowRight,
  ArrowUpRight,
  Globe,
  Mail,
  MapPin,
  Phone,
  Share2,
  MessageCircle,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import useAuthInfo from "@/store/auth";
import { AjaxGet, AjaxPostAnon } from "@/util/Ajax";
import {
  DiscoverItem,
  DiscoverService,
  searchHref,
  webUrl,
  whatsappUrl,
} from "@/lib/discover";
import styles from "../discover.module.css";
import DiscoverBusinessProfile from "./discover-business-profile";

export interface DiscoverCompany extends DiscoverItem {
  isOwner?: boolean;
  hasActiveSubscription?: boolean;
  subscriptionExpiresOn?: string;
  updatedOn?: string;
  socials: Array<{ encryptedId?: string; platform: string; url: string }>;
  gallery: string[];
  locations?: Array<{
    name?: string;
    streetAddress?: string;
    city: string;
    province?: string;
    country: string;
    postalCode?: string;
    latitude?: number;
    longitude?: number;
  }>;
  timeZoneId?: string;
  isVerified?: boolean;
  averageRating?: number;
  reviewCount?: number;
  isOpenNow?: boolean;
  hasActiveSpecial?: boolean;
  acceptsBookings?: boolean;
  tradingHours?: Array<{
    dayOfWeek: number;
    openTime?: string | null;
    closeTime?: string | null;
    isClosed: boolean;
    displayOrder?: number;
  }>;
  services?: DiscoverService[];
  specials?: Array<{
    encryptedId?: string;
    title: string;
    description: string;
    imageUrl?: string | null;
    offerLabel?: string | null;
    promoCode?: string | null;
    callToActionLabel?: string | null;
    callToActionUrl?: string | null;
    terms?: string | null;
    startsOn: string;
    endsOn: string;
  }>;
  reviewSummary?: {
    averageRating: number;
    reviewCount: number;
    distribution: Record<string, number>;
  };
  reviews?: Array<{
    encryptedId: string;
    reviewerName: string;
    rating: number;
    title?: string | null;
    body: string;
    isVerifiedClient: boolean;
    publishedOn: string;
    businessResponse?: string | null;
  }>;
  verificationTags?: Array<{
    encryptedId: string;
    name: string;
    description?: string | null;
    publicExplanation?: string | null;
    isPrimaryVerification: boolean;
    verifiedOn: string;
    expiresOn?: string | null;
  }>;
}

function LegacyDiscoverDetailClient({ company }: { company: DiscoverCompany }) {
  const apiToken = useAuthInfo((state) => state.apiToken);
  const [isOwner, setIsOwner] = useState(false);
  const [shareStatus, setShareStatus] = useState("");
  const [activeImage, setActiveImage] = useState<number | null>(null);
  const galleryDialog = useRef<HTMLDialogElement>(null);
  const viewed = useRef("");
  const usedContacts = useRef(new Set<string>());
  const gallery = company.gallery
    .map(webUrl)
    .filter((url): url is string => Boolean(url));
  const socials = company.hasActiveSubscription
    ? company.socials.filter((item) => webUrl(item.url))
    : [];
  const contacts = company.hasActiveSubscription
    ? [
        {
          label: "Email",
          value: company.email,
          href: company.email
            ? `mailto:${company.email.replace(/[\r\n]/g, "")}`
            : undefined,
          icon: Mail,
        },
        {
          label: "Phone",
          value: company.phone,
          href: company.phone
            ? `tel:${company.phone.replace(/[^\d+]/g, "")}`
            : undefined,
          icon: Phone,
        },
        {
          label: "Whatsapp",
          value: company.whatsapp,
          href: whatsappUrl(company.whatsapp),
          icon: MessageCircle,
        },
        {
          label: "Website",
          value: company.website,
          href: webUrl(company.website),
          icon: Globe,
        },
      ].filter((item) => item.value && item.href)
    : [];

  const track = (eventType: string) =>
    void AjaxPostAnon("Discover/Analytics", {
      encListingId: company.encryptedId,
      eventType,
      referrerUrl: window.location.href,
    });
  useEffect(() => {
    void useAuthInfo.persist.rehydrate();
  }, []);
  useEffect(() => {
    let current = true;
    setIsOwner(false);
    if (apiToken)
      void AjaxGet({ apiToken }, `Discover/Owner/${company.encryptedId}`).then(
        (response) => {
          if (current) setIsOwner(Boolean(response?.data?.isOwner));
        },
      );
    return () => {
      current = false;
    };
  }, [apiToken, company.encryptedId]);
  useEffect(() => {
    if (viewed.current === company.encryptedId) return;
    viewed.current = company.encryptedId;
    usedContacts.current.clear();
    track("View");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company.encryptedId]);
  useEffect(() => {
    if (activeImage === null) {
      galleryDialog.current?.close();
      return;
    }
    galleryDialog.current?.showModal();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [activeImage]);
  const changeImage = (direction: number) =>
    setActiveImage((index) =>
      index === null
        ? null
        : (index + direction + gallery.length) % gallery.length,
    );
  const share = async () => {
    try {
      if (navigator.share)
        await navigator.share({
          title: company.title,
          url: window.location.href,
        });
      else {
        await navigator.clipboard.writeText(window.location.href);
        setShareStatus("Profile link copied.");
      }
    } catch (error) {
      if (!(error instanceof Error && error.name === "AbortError"))
        setShareStatus(
          "Copy the address from your browser to share this profile.",
        );
    }
  };

  return (
    <main className={styles.page}>
      <div className={styles.container}>
        <nav className={styles.breadcrumbs} aria-label="Breadcrumb">
          <Link href="/">Home</Link>
          <span aria-hidden="true">/</span>
          <Link href="/discover">Discover</Link>
          <span aria-hidden="true">/</span>
          <Link href={searchHref({ industry: company.industry })}>
            {company.industry}
          </Link>
          <span aria-hidden="true">/</span>
          <span aria-current="page">{company.title}</span>
        </nav>
        <header className={styles.profileHero}>
          <div className={styles.profileBanner} aria-hidden="true" />
          <div className={styles.profileHeader}>
            <div className={styles.profileTop}>
              <div className={styles.logo}>
                {webUrl(company.imageUrl) ? (
                  <img
                    src={webUrl(company.imageUrl)}
                    alt={`${company.title} logo`}
                    width={120}
                    height={120}
                  />
                ) : (
                  <span aria-hidden="true">
                    {company.title.slice(0, 2).toUpperCase()}
                  </span>
                )}
              </div>
              <button
                type="button"
                className={styles.secondary}
                onClick={share}
              >
                <Share2 size={16} aria-hidden="true" />
                Share profile
              </button>
            </div>
            <Link
              className={styles.badge}
              href={searchHref({ industry: company.industry })}
            >
              {company.industry}
            </Link>
            <h1>{company.title}</h1>
            {company.location && (
              <p className={styles.muted}>
                <MapPin size={16} className="mr-1 inline" aria-hidden="true" />
                {company.location}
              </p>
            )}
            <div className={styles.chips}>
              {company.isRemote && (
                <span className={styles.chip}>
                  <Globe size={14} aria-hidden="true" />
                  Remote services available
                </span>
              )}
              {contacts.length > 0 && (
                <a href="#business-contact" className={styles.button}>
                  Get in touch <ArrowRight size={16} aria-hidden="true" />
                </a>
              )}
              {gallery.length > 0 && (
                <a href="#business-gallery" className={styles.secondary}>
                  View gallery ({gallery.length})
                </a>
              )}
              {isOwner && (
                <Link
                  className={styles.secondary}
                  href={`/discover/${company.encryptedId}/admin`}
                >
                  Manage your business
                </Link>
              )}
            </div>
            <p role="status" aria-live="polite" className={styles.muted}>
              {shareStatus}
            </p>
          </div>
        </header>
        <div className={styles.profileLayout}>
          <div className={styles.stack}>
            <section className={styles.panel}>
              <p className={styles.eyebrow}>About</p>
              <h2>About {company.title}</h2>
              <p className={styles.about}>
                {company.description ||
                  `${company.title} is listed in the ${company.industry} industry${company.location ? ` in ${company.location}` : ""}. Get in touch to learn more about its services.`}
              </p>
            </section>
            {gallery.length > 0 && (
              <section className={styles.panel} id="business-gallery">
                <p className={styles.eyebrow}>A closer look</p>
                <h2>Inside {company.title}</h2>
                <div className={styles.gallery}>
                  {gallery.map((image, index) => (
                    <button
                      type="button"
                      key={`${image}-${index}`}
                      onClick={() => setActiveImage(index)}
                      aria-label={`Open photo ${index + 1} of ${gallery.length}`}
                      aria-haspopup="dialog"
                    >
                      <figure>
                        <img
                          src={image}
                          alt={`${company.title} gallery photo ${index + 1}`}
                          loading="lazy"
                          decoding="async"
                          width={400}
                          height={210}
                        />
                        <figcaption>
                          Photo {index + 1}{" "}
                          <ArrowUpRight
                            size={13}
                            className="inline"
                            aria-hidden="true"
                          />
                        </figcaption>
                      </figure>
                    </button>
                  ))}
                </div>
              </section>
            )}
            {Boolean(company.locations?.length) && (
              <section className={styles.panel}>
                <h2>Where to find us</h2>
                {company.locations!.map((location, index) => (
                  <address className={styles.address} key={index}>
                    {location.name && (
                      <strong>
                        {location.name}
                        <br />
                      </strong>
                    )}
                    {location.streetAddress && (
                      <>
                        {location.streetAddress}
                        <br />
                      </>
                    )}
                    {[
                      location.city,
                      location.province,
                      location.postalCode,
                      location.country,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                    <br />
                    <a
                      className={styles.textLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([location.streetAddress, location.city, location.province, location.country].filter(Boolean).join(", "))}`}
                    >
                      View on map{" "}
                      <ArrowUpRight
                        size={13}
                        className="inline"
                        aria-hidden="true"
                      />
                    </a>
                  </address>
                ))}
              </section>
            )}
            <section className={styles.panel}>
              <h2>Keep exploring</h2>
              <p className={styles.muted}>
                Find more businesses and services in this industry.
              </p>
              <div className={styles.chips}>
                <Link
                  href={searchHref({ industry: company.industry })}
                  className={styles.secondary}
                >
                  More in {company.industry}
                  <ArrowRight size={16} aria-hidden="true" />
                </Link>
                {company.location && (
                  <Link
                    href={searchHref({ location: company.location })}
                    className={styles.textLink}
                  >
                    Businesses in {company.location}
                  </Link>
                )}
              </div>
            </section>
          </div>
          <aside className={styles.stack}>
            <section className={styles.panel} id="business-contact">
              <p className={styles.eyebrow}>Start a conversation</p>
              <h2>Connect with {company.title}</h2>
              {contacts.length ? (
                <>
                  <p className={styles.muted}>
                    Choose the way that works for you.
                  </p>
                  {contacts.map((contact) => (
                    <a
                      className={styles.contact}
                      key={contact.label}
                      href={contact.href}
                      target={
                        contact.href?.startsWith("http") ? "_blank" : undefined
                      }
                      rel={
                        contact.href?.startsWith("http")
                          ? "noopener noreferrer ugc"
                          : undefined
                      }
                      onClick={() => {
                        if (!usedContacts.current.has(contact.label)) {
                          usedContacts.current.add(contact.label);
                          track(`${contact.label}Reveal`);
                        }
                      }}
                    >
                      <contact.icon
                        size={20}
                        aria-hidden="true"
                        className="shrink-0"
                      />
                      <span>
                        <small>
                          {contact.label === "Whatsapp"
                            ? "WhatsApp"
                            : contact.label}
                        </small>
                        <strong>
                          {contact.label === "Whatsapp"
                            ? "Chat on WhatsApp"
                            : contact.value}
                        </strong>
                      </span>
                      <ArrowUpRight
                        size={16}
                        aria-hidden="true"
                        className="ml-auto shrink-0"
                      />
                    </a>
                  ))}
                </>
              ) : (
                <p className={styles.notice}>
                  Contact details are currently unavailable for this business.
                </p>
              )}
            </section>
            {socials.length > 0 && (
              <section className={styles.panel}>
                <h2>Find us online</h2>
                {socials.map((social, index) => (
                  <a
                    className={styles.contact}
                    key={social.encryptedId || index}
                    href={webUrl(social.url)}
                    target="_blank"
                    rel="noopener noreferrer ugc"
                    onClick={() => {
                      if (social.encryptedId)
                        void AjaxPostAnon("Discover/Social/Analytics", {
                          encListingId: company.encryptedId,
                          encSocialId: social.encryptedId,
                          eventType: "Click",
                          referrerUrl: window.location.href,
                        });
                    }}
                  >
                    <Globe size={18} aria-hidden="true" />
                    <strong>{social.platform}</strong>
                    <ArrowUpRight
                      size={16}
                      className="ml-auto"
                      aria-hidden="true"
                    />
                  </a>
                ))}
              </section>
            )}
          </aside>
        </div>
        <dialog
          className={styles.dialog}
          ref={galleryDialog}
          aria-labelledby="gallery-dialog-title"
          onCancel={() => setActiveImage(null)}
          onClose={() => setActiveImage(null)}
          onClick={(event) => {
            if (event.target === event.currentTarget) setActiveImage(null);
          }}
          onKeyDown={(event) => {
            if (event.key === "ArrowRight") {
              event.preventDefault();
              changeImage(1);
            }
            if (event.key === "ArrowLeft") {
              event.preventDefault();
              changeImage(-1);
            }
          }}
        >
          <div className={styles.dialogBar}>
            <p id="gallery-dialog-title">
              {company.title} · Photo {(activeImage ?? 0) + 1} of{" "}
              {gallery.length}
            </p>
            <button
              type="button"
              onClick={() => setActiveImage(null)}
              aria-label="Close gallery"
            >
              <X size={20} />
            </button>
          </div>
          {activeImage !== null && (
            <img
              src={gallery[activeImage]}
              alt={`${company.title} gallery photo ${activeImage + 1}`}
            />
          )}
          {gallery.length > 1 && (
            <div className={styles.dialogBar}>
              <button
                type="button"
                onClick={() => changeImage(-1)}
                aria-label="Previous photo"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                type="button"
                onClick={() => changeImage(1)}
                aria-label="Next photo"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          )}
        </dialog>
      </div>
    </main>
  );
}

export default function DiscoverDetailClient({
  company,
}: {
  company: DiscoverCompany;
}) {
  return <DiscoverBusinessProfile company={company} />;
}